/**
 * Agent BAT — Super-admin recovery console.
 *
 * When a PS1 installer cannot confirm the agent service started (120s poll),
 * it opens a recovery session on the server and stays in a command loop.
 * This page lets the super admin:
 *   • See all sessions (active/closed/expired) with live PS1 heartbeat status
 *   • Click a session to open a split-pane terminal view
 *   • Send arbitrary PowerShell commands or use quick-action buttons
 *   • Extend the session TTL or close the session cleanly
 *
 * Live output is delivered via SSE: GET /bat-rcmd/{code}/stream
 */
import React, {
  useState, useEffect, useCallback, useRef, useMemo,
} from 'react';
import { useAuth } from '../store/auth';
import toast from 'react-hot-toast';

const API = process.env.REACT_APP_SERVER_URL || '';
const REFRESH_MS = 10_000;   // session list auto-refresh

// ── Quick commands the super admin can fire with one click ────────────────────
const QUICK_CMDS = [
  { label: 'Check service',       cmd: (svc) => `Get-Service "${svc}" -EA SilentlyContinue | Select Name,Status,StartType | ConvertTo-Json` },
  { label: 'Kill MSI lock',       cmd: () => "Get-Job|Stop-Job -PassThru|Remove-Job -Force -EA 0;Start-Sleep 2;Remove-Item 'C:\\Windows\\Temp\\rmm_s.msi','C:\\Windows\\Temp\\rmm_s.msi.total','C:\\Windows\\Temp\\rmm_s.msi.err' -Force -EA 0;'Lock cleared — run Retry download'" },
  { label: 'Check $url',         cmd: () => "try{$req=[Net.HttpWebRequest]::Create($url);$req.Method='HEAD';$req.Timeout=10000;[Net.ServicePointManager]::SecurityProtocol=[Net.SecurityProtocolType]::Tls12;$resp=$req.GetResponse();'URL OK: '+$resp.StatusCode+' size='+[math]::Round($resp.ContentLength/1MB,1)+'MB';$resp.Close()}catch{'URL FAIL: '+$_.Exception.Message+' -- if 404, trigger a new build from Agent Installer then retry'}" },
  { label: 'Refresh $url',       cmd: (_, code) => `[Net.ServicePointManager]::SecurityProtocol=[Net.SecurityProtocolType]::Tls12;$_fu=try{(Invoke-WebRequest -Uri 'https://rmm.docmail.cloud/bat-rcmd/${code}/fresh-url' -UseBasicParsing -TimeoutSec 10 -EA Stop).Content|ConvertFrom-Json}catch{$null};if($_fu -and $_fu.url){$url=$_fu.url;'$url refreshed to: '+$url}else{'Could not fetch fresh URL — check server'}` },
  { label: 'Retry download',      cmd: () => "Get-Job|Stop-Job -PassThru|Remove-Job -Force -EA 0;Start-Sleep 2;Remove-Item $msi,($msi+'.total'),($msi+'.err') -Force -EA 0;[Net.ServicePointManager]::SecurityProtocol=[Net.SecurityProtocolType]::Tls12;Send-Ping 'DOWNLOAD_STARTED';$dlJob=Start-Job -ScriptBlock{param($u,$f);[Net.ServicePointManager]::SecurityProtocol=[Net.SecurityProtocolType]::Tls12;$rq=[Net.HttpWebRequest]::Create($u);$rq.Timeout=-1;$rq.ReadWriteTimeout=-1;$rq.KeepAlive=$false;$fs=$null;try{$rp=$rq.GetResponse();[IO.File]::WriteAllText($f+'.total',$rp.ContentLength.ToString());$rs=$rp.GetResponseStream();$fs=[IO.File]::OpenWrite($f);$buf=New-Object byte[] 65536;while(($rd=$rs.Read($buf,0,$buf.Length)) -gt 0){$fs.Write($buf,0,$rd)};$fs.Close();$rs.Close();$rp.Close();return @{ok=$true}}catch{if($fs){try{$fs.Close()}catch{}};return @{ok=$false;err=$_.Exception.Message}}} -ArgumentList $url,$msi;$dlT0=Get-Date;$dlLastPing=Get-Date;$dlLast=-1;$dlTotal=0;$dlTotalMB='?';while($dlJob.State -eq 'Running'){Start-Sleep 8;$dlBytes=try{(Get-Item $msi -EA SilentlyContinue).Length}catch{0};if(-not $dlBytes){$dlBytes=0};if($dlTotal -eq 0){$dlSzChk=try{[long](Get-Content ($msi+'.total') -EA Stop)}catch{0};if($dlSzChk -gt 0){$dlTotal=$dlSzChk;$dlTotalMB=[math]::Round($dlSzChk/1MB,1)}};$dlMB=[math]::Round($dlBytes/1MB,1);$dlSec=[math]::Max(1,((Get-Date)-$dlT0).TotalSeconds);$dlKBs=[math]::Round($dlBytes/1KB/$dlSec,1);if($dlTotal -gt 0){$dlPct=[int]($dlBytes*100/$dlTotal);foreach($m in @(1,5,10,25,50,75,90)){if($dlPct -ge $m -and $dlLast -lt $m){Send-Ping 'DOWNLOAD_PROGRESS' ($m.ToString()+'% ('+$dlMB+'MB/'+$dlTotalMB+'MB @ '+$dlKBs.ToString()+'KB/s)');$dlLast=$m;$dlLastPing=Get-Date}}};if(((Get-Date)-$dlLastPing).TotalSeconds -ge 30){Send-Ping 'DOWNLOAD_PROGRESS' ($dlMB.ToString()+'MB @ '+$dlKBs.ToString()+'KB/s');$dlLastPing=Get-Date}};$dlJob|Wait-Job|Out-Null;$dlResult=Receive-Job $dlJob;Remove-Job $dlJob -Force -EA SilentlyContinue;Remove-Item ($msi+'.total') -Force -EA SilentlyContinue;if($dlResult -and $dlResult.ok){$sz=[math]::Round((Get-Item $msi -EA SilentlyContinue).Length/1MB,2);Send-Ping 'DOWNLOAD_OK' ($sz.ToString()+'MB');'Downloaded: '+$sz+' MB -- run Retry install'}else{$dlErr='Job failed';if($dlResult -and $dlResult.err){$dlErr=$dlResult.err};if($dlErr -like '*404*'){$dlErr='MSI not on server (404) -- trigger a new build from Agent Installer, then retry'};Send-Ping 'DOWNLOAD_FAIL' $dlErr;'FAIL: '+$dlErr}" },
  { label: 'MSI Cleanup',         cmd: (svc) => `$_si=Get-WmiObject Win32_Service -Filter "Name='${svc}'" -EA 0;$_bin=if($_si){$_si.PathName.Trim('"')}else{''};$_exe=if($_bin){[IO.Path]::GetFileName($_bin)}else{''}; $_dir=if($_bin){[IO.Path]::GetDirectoryName($_bin)}else{''};sc.exe stop "${svc}" 2>$null;sc.exe delete "${svc}" 2>$null;if($_exe){Stop-Process -Name ([IO.Path]::GetFileNameWithoutExtension($_exe)) -Force -EA 0};if($_dir){Get-Process|Where-Object{$_.Path -like "$_dir*"}|Stop-Process -Force -EA 0};Start-Sleep 2;Get-WmiObject Win32_Product -Filter "Name like '%Windows Network%'" -EA 0|ForEach-Object{$_.Uninstall()};Remove-Item "HKLM:\\SYSTEM\\CurrentControlSet\\Services\\${svc}" -Recurse -Force -EA 0;if($_dir -and (Test-Path $_dir)){Remove-Item $_dir -Recurse -Force -EA 0};foreach($b in @('HKLM:\\SOFTWARE\\Microsoft\\Windows\\CurrentVersion\\Uninstall','HKLM:\\SOFTWARE\\WOW6432Node\\Microsoft\\Windows\\CurrentVersion\\Uninstall')){Get-ChildItem $b -EA 0|Where-Object{$_.GetValue('DisplayName') -like '*Windows Network*'}|Remove-Item -Recurse -Force -EA 0};"Cleanup done EXE=[$_exe] DIR=[$_dir] — safe to install"`, danger: true },
  { label: 'Retry install',       cmd: () => "$null=Remove-Item 'C:\\Windows\\Temp\\rmm_msi.log' -Force -EA 0;$p=Start-Process msiexec -ArgumentList '/i',$msi,'/qn','/norestart','/l*v','C:\\Windows\\Temp\\rmm_msi.log' -Wait -PassThru -EA 0;\"Install exit: $($p.ExitCode) — run MSI install log for details\"" },
  { label: 'Register service',    cmd: (svc) => `$_rse=$null;foreach($_rp in @('C:\\Program Files\\Windows Network Services\\svchost_net.exe','C:\\Program Files (x86)\\Windows Network Services\\svchost_net.exe')){if(Test-Path $_rp){$_rse=$_rp;break}};if($_rse){New-Service -Name "${svc}" -BinaryPathName $_rse -StartupType Automatic -DisplayName 'Windows Network Services Host' -EA SilentlyContinue;sc.exe description "${svc}" 'Windows Network Services Host' 2>$null;Start-Service "${svc}" -EA SilentlyContinue;Start-Sleep 3;Get-Service "${svc}" -EA SilentlyContinue|Select-Object Name,Status|ConvertTo-Json}else{'EXE not found in Program Files — MSI may not have installed files yet'}` },
  { label: 'MSI install log',     cmd: () => 'Get-Content C:\\Windows\\Temp\\rmm_msi.log -Tail 50 -EA SilentlyContinue' },
  { label: 'Restart service',     cmd: (svc) => `Restart-Service "${svc}" -EA SilentlyContinue; Start-Sleep 3; Get-Service "${svc}" -EA SilentlyContinue | Select Name,Status` },
  { label: 'msiexec log',         cmd: () => 'Get-Content C:\\Windows\\Temp\\rmm_install.log -Tail 30 -EA SilentlyContinue' },
  { label: 'Disk space',          cmd: () => 'Get-PSDrive C | Select Used,Free | ConvertTo-Json' },
  { label: 'whoami / privs',      cmd: () => 'whoami /priv' },
  { label: 'Respawn +1h',         cmd: (_, code) => `__TTL__:60; # TTL extended by 1 hour — code ${code}` },
  { label: 'Respawn +3h',         cmd: (_, code) => `__TTL__:180; # TTL extended by 3 hours — code ${code}` },
  { label: 'Respawn +12h',        cmd: (_, code) => `__TTL__:720; # TTL extended by 12 hours — code ${code}` },
  { label: 'Kill session',        cmd: () => '__CLOSE__', danger: true },
];

// ── Utility ───────────────────────────────────────────────────────────────────
function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' });
}

function StatusBadge({ session }) {
  const { status, ps1_live } = session;
  if (status === 'active' && ps1_live)  return <span style={styles.badgeGreen}>● LIVE</span>;
  if (status === 'active')              return <span style={styles.badgeAmber}>● IDLE</span>;
  if (status === 'closed')              return <span style={styles.badgeGray}>CLOSED</span>;
  return <span style={styles.badgeGray}>EXPIRED</span>;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function AgentBAT() {
  const { authHeaders } = useAuth();

  const [sessions, setSessions]       = useState([]);
  const [selected, setSelected]       = useState(null);   // BatRcmdSession object
  const [terminal, setTerminal]       = useState([]);     // [{ts, type, text|cmd|output}]
  const [cmdInput, setCmdInput]       = useState('');
  const [sending, setSending]         = useState(false);
  const [extending, setExtending]     = useState(false);
  const [closing, setClosing]         = useState(false);
  const [activeQuick, setActiveQuick] = useState(null);  // label of last-clicked quick btn

  const termRef   = useRef(null);
  const sseRef    = useRef(null);
  const refreshRef = useRef(null);

  // ── Fetch session list ─────────────────────────────────────────────────────
  const loadSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/bat-rcmd/sessions`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setSessions(data);

      // Keep selected in sync with updated data
      setSelected(prev => {
        if (!prev) return null;
        return data.find(s => s.id === prev.id) || prev;
      });
    } catch { /* network hiccup — keep stale data */ }
  }, [authHeaders]);

  useEffect(() => {
    loadSessions();
    refreshRef.current = setInterval(loadSessions, REFRESH_MS);
    return () => clearInterval(refreshRef.current);
  }, [loadSessions]);

  // ── SSE stream for selected session ───────────────────────────────────────
  const openSSE = useCallback((code) => {
    if (sseRef.current) { sseRef.current.close(); sseRef.current = null; }
    setTerminal([]);

    const token = localStorage.getItem('token');
    // Append JWT as query param — SSE doesn't support custom headers in browser
    const src = new EventSource(`${API}/bat-rcmd/${code}/stream?token=${encodeURIComponent(token || '')}`);
    sseRef.current = src;

    src.onmessage = (e) => {
      try {
        const entry = JSON.parse(e.data);
        setTerminal(prev => {
          // Replace matching local echo with the server-confirmed entry (dedup)
          if (entry.type === 'cmd') {
            for (let i = prev.length - 1; i >= 0; i--) {
              if (prev[i]._local && prev[i].text === entry.text) {
                const next = [...prev];
                next[i] = entry;
                return next;
              }
            }
          }
          return [...prev, entry];
        });
      } catch { /* ignore malformed */ }
    };

    src.onerror = () => { /* connection drop — SSE auto-reconnects */ };
  }, []);

  useEffect(() => {
    if (selected) {
      openSSE(selected.code);
    } else {
      if (sseRef.current) { sseRef.current.close(); sseRef.current = null; }
    }
    return () => { if (sseRef.current) { sseRef.current.close(); sseRef.current = null; } };
  }, [selected?.id]);   // only re-open when the selected session changes

  // Auto-scroll terminal
  useEffect(() => {
    if (termRef.current) {
      termRef.current.scrollTop = termRef.current.scrollHeight;
    }
  }, [terminal]);

  // ── Send command ───────────────────────────────────────────────────────────
  const sendCmd = useCallback(async (cmd, quickLabel = null) => {
    if (!selected || !cmd.trim()) return;
    setSending(true);
    if (quickLabel) setActiveQuick(quickLabel);
    try {
      // Handle TTL extension special commands locally → call /extend instead
      const ttlMatch = cmd.match(/^__TTL__:(\d+)/);
      if (ttlMatch) {
        const addMin = parseInt(ttlMatch[1], 10);
        const res = await fetch(`${API}/bat-rcmd/${selected.code}/extend`, {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ add_minutes: addMin }),
        });
        const data = await res.json();
        if (res.ok) toast.success(`TTL → ${data.new_ttl_minutes} min`);
        else toast.error(data.detail || 'Extend failed');
        return;
      }

      // Immediately echo the command into the terminal so there's instant visual
      // feedback. The SSE stream delivers a server-confirmed 'cmd' entry shortly
      // after; the onmessage dedup handler replaces this local echo with it.
      setTerminal(prev => [...prev, { ts: new Date().toISOString(), type: 'cmd', text: cmd, _local: true }]);

      const res = await fetch(`${API}/bat-rcmd/${selected.code}/cmd`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ cmd }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Server error ${res.status}`);
      } else {
        toast.success('Command queued — waiting for PS1 to pick up (~8s)');
      }
    } catch (err) {
      toast.error(`Network error: ${err.message}`);
    } finally {
      setSending(false);
      setCmdInput('');
      setTimeout(() => setActiveQuick(null), 3000);
    }
  }, [selected, authHeaders]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendCmd(cmdInput);
    }
  };

  // ── Close session ──────────────────────────────────────────────────────────
  const closeSession = useCallback(async () => {
    if (!selected) return;
    if (!window.confirm('Close this recovery session? The PS1 will exit.')) return;
    setClosing(true);
    try {
      const res = await fetch(`${API}/bat-rcmd/${selected.code}/close`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (res.ok) {
        toast.success('Session closed');
        await loadSessions();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Close failed');
      }
    } finally { setClosing(false); }
  }, [selected, authHeaders, loadSessions]);

  // ── Extend TTL ─────────────────────────────────────────────────────────────
  const extendTtl = useCallback(async (minutes) => {
    if (!selected) return;
    setExtending(true);
    try {
      const res = await fetch(`${API}/bat-rcmd/${selected.code}/extend`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ add_minutes: minutes }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) toast.success(`TTL extended to ${data.new_ttl_minutes} min`);
      else toast.error(data.detail || 'Extend failed');
    } finally { setExtending(false); }
  }, [selected, authHeaders]);

  // ── Render terminal line ───────────────────────────────────────────────────
  const renderLine = (entry, i) => {
    const ts = entry.ts ? new Date(entry.ts).toLocaleTimeString() : '';
    if (entry.type === 'system') {
      return (
        <div key={i} style={styles.lineSystem}>
          <span style={styles.ts}>{ts}</span>
          <span style={styles.sysText}>{entry.text}</span>
        </div>
      );
    }
    if (entry.type === 'cmd') {
      return (
        <div key={i} style={styles.lineCmd}>
          <span style={styles.ts}>{ts}</span>
          <span style={styles.prompt}>PS&gt; </span>
          <span style={styles.cmdText}>{entry.text}</span>
        </div>
      );
    }
    if (entry.type === 'result') {
      return (
        <div key={i} style={styles.lineResult}>
          <span style={styles.ts}>{ts}</span>
          <pre style={styles.outputPre}>{entry.output}</pre>
        </div>
      );
    }
    return null;
  };

  // ── Derived values ─────────────────────────────────────────────────────────
  const activeSessions  = useMemo(() => sessions.filter(s => s.status === 'active'), [sessions]);
  const closedSessions  = useMemo(() => sessions.filter(s => s.status !== 'active'), [sessions]);
  const sessionSvcName  = selected?.failure_reason?.includes('agent_start_timeout')
    ? 'WinNetSvcHost'
    : (selected?.hostname ? 'WinNetSvcHost' : 'WinNetSvcHost');

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={styles.root}>
      {/* ── Left panel — session list ── */}
      <div style={styles.leftPanel}>
        <div style={styles.panelHeader}>
          <span>Recovery Sessions</span>
          <button style={styles.refreshBtn} onClick={loadSessions}>↻</button>
        </div>

        {activeSessions.length > 0 && (
          <>
            <div style={styles.sectionLabel}>ACTIVE</div>
            {activeSessions.map(s => (
              <SessionCard
                key={s.id}
                session={s}
                selected={selected?.id === s.id}
                onClick={() => setSelected(s)}
              />
            ))}
          </>
        )}

        {closedSessions.length > 0 && (
          <>
            <div style={styles.sectionLabel}>CLOSED / EXPIRED</div>
            {closedSessions.map(s => (
              <SessionCard
                key={s.id}
                session={s}
                selected={selected?.id === s.id}
                onClick={() => setSelected(s)}
              />
            ))}
          </>
        )}

        {sessions.length === 0 && (
          <div style={styles.emptyMsg}>No recovery sessions yet.<br />Sessions appear here when a PS1 installer fails to start the agent service.</div>
        )}
      </div>

      {/* ── Right panel — terminal ── */}
      <div style={styles.rightPanel}>
        {!selected ? (
          <div style={styles.noSelection}>
            Select a session on the left to open the recovery console.
          </div>
        ) : (
          <>
            {/* Session info bar */}
            <div style={styles.infoBar}>
              <span style={styles.infoHost}>{selected.hostname || 'Unknown host'}</span>
              <span style={styles.infoDetail}>{selected.ip_address}</span>
              <span style={styles.infoDetail}>{selected.os_version}</span>
              <span style={styles.infoDetail}>Code: <code>{selected.code}</code></span>
              <span style={styles.infoDetail}>TTL: {selected.ttl_minutes} min</span>
              <StatusBadge session={selected} />
              <span style={{ flex: 1 }} />
              {selected.status === 'active' && (
                <>
                  <button style={styles.extBtn} disabled={extending} onClick={() => extendTtl(60)}>+1h</button>
                  <button style={styles.extBtn} disabled={extending} onClick={() => extendTtl(180)}>+3h</button>
                  <button style={styles.extBtn} disabled={extending} onClick={() => extendTtl(720)}>+12h</button>
                  <button style={styles.closeBtn} disabled={closing} onClick={closeSession}>
                    {closing ? '...' : 'Close session'}
                  </button>
                </>
              )}
            </div>

            {/* Quick commands */}
            {selected.status === 'active' && (
              <div style={styles.quickBar}>
                {QUICK_CMDS.map(({ label, cmd, danger }) => (
                  <button
                    key={label}
                    style={
                      activeQuick === label ? styles.quickBtnActive :
                      danger ? styles.quickBtnDanger : styles.quickBtn
                    }
                    disabled={sending}
                    onClick={() => sendCmd(cmd(sessionSvcName, selected.code), label)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}

            {/* Terminal output */}
            <div style={styles.terminal} ref={termRef}>
              {terminal.map(renderLine)}
              {terminal.length === 0 && (
                <div style={styles.termWaiting}>Waiting for output…</div>
              )}
            </div>

            {/* Command input */}
            {selected.status === 'active' && (
              <div style={styles.inputRow}>
                <span style={styles.promptLabel}>PS&gt;</span>
                <textarea
                  style={styles.cmdTextarea}
                  rows={2}
                  placeholder="Type a PowerShell command (Enter to send, Shift+Enter for newline)"
                  value={cmdInput}
                  onChange={e => setCmdInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={sending}
                />
                <button
                  style={styles.sendBtn}
                  disabled={sending || !cmdInput.trim()}
                  onClick={() => sendCmd(cmdInput)}
                >
                  {sending ? '…' : 'Send'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── SessionCard ───────────────────────────────────────────────────────────────
function SessionCard({ session, selected, onClick }) {
  return (
    <div
      style={{ ...styles.card, ...(selected ? styles.cardSelected : {}) }}
      onClick={onClick}
    >
      <div style={styles.cardTop}>
        <span style={styles.cardHost}>{session.hostname || 'Unknown'}</span>
        <StatusBadge session={session} />
      </div>
      <div style={styles.cardMeta}>
        <span>{session.ip_address}</span>
        <span style={{ marginLeft: 8 }}>Code: <code>{session.code}</code></span>
      </div>
      <div style={styles.cardMeta}>Admin: {session.admin_name}</div>
      <div style={styles.cardMeta}>Opened: {fmt(session.opened_at)}</div>
      {session.failure_reason && (
        <div style={styles.cardReason}>{session.failure_reason}</div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  root: {
    display: 'flex',
    height: 'calc(100vh - 56px)',
    background: '#0f1117',
    color: '#e2e8f0',
    fontFamily: 'monospace',
    overflow: 'hidden',
  },
  leftPanel: {
    width: 300,
    minWidth: 260,
    borderRight: '1px solid #1e293b',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    background: '#0d1117',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    fontWeight: 700,
    fontSize: 13,
    borderBottom: '1px solid #1e293b',
    color: '#94a3b8',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  refreshBtn: {
    background: 'none',
    border: 'none',
    color: '#64748b',
    cursor: 'pointer',
    fontSize: 18,
  },
  sectionLabel: {
    padding: '8px 16px 2px',
    fontSize: 10,
    color: '#475569',
    letterSpacing: 1.5,
    fontWeight: 700,
  },
  card: {
    padding: '10px 14px',
    borderBottom: '1px solid #1e293b',
    cursor: 'pointer',
    transition: 'background 0.1s',
  },
  cardSelected: {
    background: '#1e2d3d',
  },
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardHost: { fontWeight: 600, fontSize: 13 },
  cardMeta: { fontSize: 11, color: '#64748b', marginTop: 2 },
  cardReason: { fontSize: 11, color: '#f97316', marginTop: 3 },
  emptyMsg: {
    padding: 20,
    fontSize: 12,
    color: '#475569',
    lineHeight: 1.6,
  },
  rightPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  noSelection: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#475569',
    fontSize: 14,
  },
  infoBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 16px',
    background: '#1a2332',
    borderBottom: '1px solid #1e293b',
    flexWrap: 'wrap',
    fontSize: 12,
  },
  infoHost: { fontWeight: 700, fontSize: 14 },
  infoDetail: { color: '#94a3b8' },
  extBtn: {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 4,
    color: '#94a3b8',
    cursor: 'pointer',
    padding: '3px 10px',
    fontSize: 12,
  },
  closeBtn: {
    background: '#7f1d1d',
    border: '1px solid #991b1b',
    borderRadius: 4,
    color: '#fca5a5',
    cursor: 'pointer',
    padding: '3px 12px',
    fontSize: 12,
  },
  quickBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    padding: '8px 12px',
    borderBottom: '1px solid #1e293b',
    background: '#111827',
  },
  quickBtn: {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 4,
    color: '#e2e8f0',
    cursor: 'pointer',
    padding: '4px 10px',
    fontSize: 11,
  },
  quickBtnDanger: {
    background: '#7f1d1d',
    border: '1px solid #991b1b',
    borderRadius: 4,
    color: '#fca5a5',
    cursor: 'pointer',
    padding: '4px 10px',
    fontSize: 11,
  },
  quickBtnActive: {
    background: '#1d4ed8',
    border: '1px solid #3b82f6',
    borderRadius: 4,
    color: '#fff',
    cursor: 'pointer',
    padding: '4px 10px',
    fontSize: 11,
    fontWeight: 700,
  },
  terminal: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 16px',
    background: '#0a0e1a',
    fontSize: 12,
    lineHeight: 1.6,
  },
  termWaiting: { color: '#475569', fontStyle: 'italic' },
  ts: { color: '#334155', marginRight: 8, fontSize: 11 },
  lineSystem: { marginBottom: 4, color: '#f59e0b' },
  sysText: { fontWeight: 600 },
  lineCmd: { marginBottom: 4 },
  prompt: { color: '#22d3ee', marginRight: 4 },
  cmdText: { color: '#a5f3fc' },
  lineResult: { marginBottom: 10 },
  outputPre: {
    margin: '2px 0 0 20px',
    color: '#d1fae5',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    background: '#0d1117',
    borderLeft: '2px solid #1e293b',
    padding: '4px 8px',
    borderRadius: 3,
  },
  inputRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '10px 12px',
    borderTop: '1px solid #1e293b',
    background: '#111827',
  },
  promptLabel: { color: '#22d3ee', paddingTop: 6, fontSize: 13 },
  cmdTextarea: {
    flex: 1,
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: 4,
    color: '#e2e8f0',
    padding: '6px 10px',
    fontSize: 12,
    fontFamily: 'monospace',
    resize: 'none',
  },
  sendBtn: {
    background: '#1d4ed8',
    border: 'none',
    borderRadius: 4,
    color: '#fff',
    cursor: 'pointer',
    padding: '0 18px',
    fontWeight: 700,
    fontSize: 13,
    alignSelf: 'stretch',
  },
  badgeGreen: {
    background: '#14532d',
    color: '#86efac',
    borderRadius: 10,
    padding: '2px 8px',
    fontSize: 11,
    fontWeight: 700,
  },
  badgeAmber: {
    background: '#451a03',
    color: '#fcd34d',
    borderRadius: 10,
    padding: '2px 8px',
    fontSize: 11,
    fontWeight: 700,
  },
  badgeGray: {
    background: '#1e293b',
    color: '#64748b',
    borderRadius: 10,
    padding: '2px 8px',
    fontSize: 11,
    fontWeight: 700,
  },
};
